/**
 * Plan Term Form Modal
 *
 * Dual-purpose modal for adding or updating a subscription plan term.
 * - Add:    POST /api/payroll-platform/org-plan/add-subscription-plan-term
 * - Update: POST /api/payroll-platform/org-plan/update-subscription-plan-term
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Save } from 'lucide-react';

import { Modal, Button, Input, Select } from '@/components/common';
import { subscriptionService, ApiError } from '@/api/services';
import type {
  SubscriptionPlan,
  SubscriptionPlanTermGrid,
  AddSubscriptionPlanTermRequest,
  UpdateSubscriptionPlanTermRequest,
} from '@/api/types';
import { useToast } from '@/hooks/useToast';

// ============================================
// Options
// ============================================

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const TERM_TYPE_OPTIONS = [
  { value: 'MONTH', label: 'Month' },
  { value: 'YEAR', label: 'Year' },
];

// Currency is not user-editable; sent as a fixed default.
const DEFAULT_CURRENCY = 'INR';

// ============================================
// Form Schema
// ============================================

const positiveNumberField = z
  .string()
  .min(1, 'Required')
  .refine((val) => !isNaN(Number(val)) && Number(val) > 0, 'Must be greater than 0');

const priceField = z
  .string()
  .min(1, 'Required')
  .refine((val) => !isNaN(Number(val)) && Number(val) >= 0, 'Must be a valid amount');

const planTermSchema = z.object({
  planTermCode: z.string().min(1, 'Term code is required'),
  planTermName: z.string().min(1, 'Term name is required'),
  termType: z.enum(['MONTH', 'YEAR']),
  termValue: positiveNumberField,
  price: priceField,
  actualPrice: priceField,
  status: z.string().min(1, 'Status is required'),
});

type PlanTermFormData = z.infer<typeof planTermSchema>;

const EMPTY_VALUES: PlanTermFormData = {
  planTermCode: '',
  planTermName: '',
  termType: 'MONTH',
  termValue: '',
  price: '',
  actualPrice: '',
  status: 'ACTIVE',
};

/** Map an existing grid term into form values for editing. */
function termToFormValues(term: SubscriptionPlanTermGrid): PlanTermFormData {
  return {
    planTermCode: term.planTermCode ?? '',
    // Grid response has no name/currency — fall back to sensible defaults.
    planTermName: term.planTermCode ?? '',
    termType: term.termType?.toUpperCase() === 'YEAR' ? 'YEAR' : 'MONTH',
    termValue: String(term.termValue ?? ''),
    price: String(term.price ?? ''),
    actualPrice: String(term.actualPrice ?? ''),
    status: term.isActive ? 'ACTIVE' : 'INACTIVE',
  };
}

// ============================================
// Component Props
// ============================================

interface PlanTermFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  /** The parent plan (used for its id and title). */
  plan: SubscriptionPlan | null;
  /** When provided, the modal edits this term instead of adding a new one. */
  term?: SubscriptionPlanTermGrid | null;
}

// ============================================
// Component
// ============================================

export function PlanTermFormModal({
  open,
  onOpenChange,
  onSuccess,
  plan,
  term,
}: PlanTermFormModalProps) {
  const toast = useToast();
  const isEditMode = Boolean(term?.id);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<PlanTermFormData>({
    resolver: zodResolver(planTermSchema),
    defaultValues: EMPTY_VALUES,
  });

  const selectedStatus = watch('status');
  const selectedTermType = watch('termType');

  // Prefill (edit) or clear (add) whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    setFormError(null);
    reset(term ? termToFormValues(term) : EMPTY_VALUES);
  }, [open, term, reset]);

  const handleClose = () => {
    if (!isSubmitting) {
      reset(EMPTY_VALUES);
      setFormError(null);
      onOpenChange(false);
    }
  };

  const onSubmit = async (data: PlanTermFormData) => {
    if (!plan) return;
    setFormError(null);
    setIsSubmitting(true);
    try {
      const termPayload = {
        subscriptionPlanId: plan.id,
        planTermCode: data.planTermCode,
        planTermName: data.planTermName,
        termType: data.termType,
        termValue: Number(data.termValue),
        price: Number(data.price),
        actualPrice: Number(data.actualPrice),
        currency: DEFAULT_CURRENCY,
        isActive: data.status === 'ACTIVE',
      };

      if (isEditMode && term) {
        const request: UpdateSubscriptionPlanTermRequest = {
          planTerm: { id: term.id, ...termPayload },
        };
        await subscriptionService.updateSubscriptionPlanTerm(request);
        toast.success('Plan term updated successfully');
      } else {
        const request: AddSubscriptionPlanTermRequest = {
          planTerm: termPayload,
        };
        await subscriptionService.addSubscriptionPlanTerm(request);
        toast.success('Plan term added successfully');
      }

      reset(EMPTY_VALUES);
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      if (error instanceof ApiError) {
        setFormError(error.showMessage);
      } else {
        setFormError(
          `Failed to ${isEditMode ? 'update' : 'add'} plan term. Please try again.`
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      title={isEditMode ? 'Update Plan Term' : 'Add Plan Term'}
      description={
        plan
          ? `${isEditMode ? 'Update' : 'Add'} a billing term for "${plan.name}"`
          : 'Manage a billing term for the subscription plan'
      }
      size="lg"
      closeOnOverlayClick={!isSubmitting}
      footer={
        <>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            loading={isSubmitting}
            leftIcon={<Save className="h-4 w-4" />}
            onClick={handleSubmit(onSubmit)}
          >
            {isEditMode ? 'Update Term' : 'Add Term'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {formError && (
          <div className="rounded-lg bg-danger/10 border border-danger/20 px-4 py-3">
            <p className="text-sm text-danger dark:text-danger-dark">{formError}</p>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <Input
            label="Term Code"
            placeholder="e.g. MONTHLY"
            required
            disabled={isEditMode}
            error={errors.planTermCode?.message}
            {...register('planTermCode')}
          />
          <Input
            label="Term Name"
            placeholder="e.g. Monthly"
            required
            error={errors.planTermName?.message}
            {...register('planTermName')}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Select
            label="Term Type"
            placeholder="Select term type"
            required
            options={TERM_TYPE_OPTIONS}
            value={selectedTermType}
            onChange={(value) => setValue('termType', value as 'MONTH' | 'YEAR')}
            errorMessage={errors.termType?.message}
          />
          <Input
            label={selectedTermType === 'YEAR' ? 'Duration (Years)' : 'Duration (Months)'}
            type="number"
            placeholder={selectedTermType === 'YEAR' ? 'e.g. 1' : 'e.g. 6'}
            required
            error={errors.termValue?.message}
            {...register('termValue')}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Input
            label="Price"
            type="number"
            placeholder="e.g. 999"
            required
            error={errors.price?.message}
            {...register('price')}
          />
          <Input
            label="Actual Price"
            type="number"
            placeholder="e.g. 1299"
            required
            error={errors.actualPrice?.message}
            {...register('actualPrice')}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Select
            label="Status"
            placeholder="Select status"
            required
            options={STATUS_OPTIONS}
            value={selectedStatus}
            onChange={(value) => setValue('status', value)}
            errorMessage={errors.status?.message}
          />
        </div>
      </form>
    </Modal>
  );
}

export default PlanTermFormModal;
